import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  FileText, Upload, Download, Eye, CheckCircle, XCircle, 
  AlertTriangle, Clock, History, Trash2, ArrowRight, 
  RefreshCw, ChevronDown, ChevronRight, Loader2, Send,
  FileSearch, Archive, MoreVertical, Stamp
} from 'lucide-react';
import { formatBackendDate } from '../../lib/dateUtils';
import DocumentActionMenu from './DocumentActionMenu';
import { isFileUnavailableForInspection } from './DocumentActionMenu';
import {
  fetchProtectedFileBlob,
  downloadBlobUrl,
  revokeBlobUrlLater,
} from '../../lib/protectedFiles';
import { isPreviewableFile } from './complianceRequirementMap';
import API_BASE from '../../utils/apiBase';
import { canViewDocument, resolveRequirementFilePreviewUrl, resolveRequirementFileDownloadUrl } from '../../lib/resolveDocumentFetchUrl';

const API = API_BASE;

/**
 * RequirementFilesDrawer - Shows all files for a requirement with full lifecycle data
 * 
 * Features:
 * - Active files with metadata, actions, request linkage
 * - Historical files (superseded, rejected, uploaded_in_error, moved) - collapsed
 * - Per-file actions via DocumentActionMenu
 * - Immediate refresh when file status changes
 */
export default function RequirementFilesDrawer({
  open,
  onClose,
  employeeId,
  requirementKey,
  requirementTitle,
  onRefresh,
  onUpload,
  onRequest,
  onPreviewFile,
  onExtractReview,
  isAuditor = false
}) {
  const [loading, setLoading] = useState(false);
  const [filesData, setFilesData] = useState(null);
  const [historicalExpanded, setHistoricalExpanded] = useState(false);
  const [requestHistoryExpanded, setRequestHistoryExpanded] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, file: null });
  const [actionReason, setActionReason] = useState('');
  const [newRequirementId, setNewRequirementId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { token } = useAuth();

  // Handle file view - preview-first logic
  const handleViewFile = async (file) => {
    if (!file) {
      toast.error('File data not available');
      return;
    }
    if (isFileUnavailableForInspection(file)) {
      toast.error('This file is unavailable for inspection. Use the requirement-specific replacement or policy workflow.');
      return;
    }

    const previewDoc = {
      document_id: file.file_id,
      file_id: file.file_id,
      file_url: file.stamped_file_url || file.file_url,
      stamped_file_url: file.stamped_file_url,
      verified: file.is_verified || file.verified || Boolean(file.stamped_file_url),
      verification_stamp: file.verification_stamp,
      view_url: file.view_url,
      download_url: file.download_url,
    };

    if (!canViewDocument(previewDoc) && !file.file_available) {
      toast.error('No viewable file is linked to this record. Its stored copy has not been checked; do not ask for re-upload yet.');
      return;
    }

    const fetchUrl = resolveRequirementFilePreviewUrl(previewDoc, { apiBase: API });
    const isStamped = !!file.stamped_file_url;
    const mimeType = file.mime_type || file.content_type || '';

    if (fetchUrl && isPreviewableFile(file) && onPreviewFile) {
      onPreviewFile({
        file_url: fetchUrl,
        file_name: isStamped ? `[STAMPED] ${file.file_name || file.file_label || 'Document'}` : (file.file_name || file.file_label || 'Document'),
        mime_type: mimeType,
        file_id: file.file_id,
        verification_stamp_by_name: file.verification_stamp_by_name,
        verification_stamp_at: file.verification_stamp_at,
      });
    } else {
      await handleDownloadFile(file);
    }
  };

  // Handle file download
  const handleDownloadFile = async (file) => {
    if (isFileUnavailableForInspection(file)) {
      toast.error('This file is unavailable for inspection. Use the requirement-specific replacement or policy workflow.');
      return;
    }
    const fileUrl = resolveRequirementFileDownloadUrl(file, { apiBase: API });
    if (!file || !fileUrl) {
      toast.error('No authenticated file route is linked to this record. Check evidence recovery before requesting a replacement.');
      return;
    }

    try {
      const { blobUrl } = await fetchProtectedFileBlob(fileUrl, token);
      downloadBlobUrl(blobUrl, file.file_name || file.file_label || 'document');
      revokeBlobUrlLater(blobUrl);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Document access was denied. Check your session and permissions; this does not mean the certificate expired.');
      } else if (status === 404 || status === 410) {
        toast.error('The saved file could not be retrieved. Check historical evidence or request a current copy after review.');
      } else {
        toast.error('Could not download the file. Check the connection and retry; no certificate status was changed.');
      }
    }
  };

  // Fetch files data when drawer opens
  useEffect(() => {
    if (open && employeeId && requirementKey) {
      fetchFiles();
    }
  }, [open, employeeId, requirementKey]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/employees/${employeeId}/requirements/${requirementKey}/files`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFilesData(response.data);
    } catch (err) {
      toast.error('Failed to load files');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle file actions
  const handleMarkUploadedInError = async (fileId) => {
    if (!actionReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(
        `${API}/documents/${fileId}/mark-uploaded-in-error`,
        { reason: actionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('File marked as uploaded in error');
      setActionDialog({ open: false, type: null, file: null });
      setActionReason('');
      fetchFiles(); // Refresh to move file to historical
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark file');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSupersede = async (fileId) => {
    if (!actionReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(
        `${API}/documents/${fileId}/supersede`,
        { reason: actionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('File superseded');
      setActionDialog({ open: false, type: null, file: null });
      setActionReason('');
      fetchFiles();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to supersede file');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMoveCategory = async (fileId) => {
    if (!actionReason.trim() || !newRequirementId) {
      toast.error('Please provide a reason and select new category');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(
        `${API}/documents/${fileId}/move-category`,
        { reason: actionReason, new_requirement_id: newRequirementId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('File moved to new category');
      setActionDialog({ open: false, type: null, file: null });
      setActionReason('');
      setNewRequirementId('');
      fetchFiles();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move file');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (fileId) => {
    setIsSubmitting(true);
    try {
      await axios.post(
        `${API}/documents/${fileId}/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('File verified');
      fetchFiles();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify file');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestReplacement = async (fileId) => {
    if (!actionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setIsSubmitting(true);
    try {
      // Use the correct endpoint with email notification
      await axios.post(
        `${API}/employee-documents/${fileId}/request-replacement`,
        { reason: actionReason, notify_employee: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Replacement requested - employee notified');
      setActionDialog({ open: false, type: null, file: null });
      setActionReason('');
      fetchFiles();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request replacement');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove verification stamp from a file
  const handleRemoveStamp = async (fileId) => {
    setIsSubmitting(true);
    try {
      await axios.delete(
        `${API}/employee-documents/${fileId}/verification-stamp`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stamp removed - document reverted to original');
      fetchFiles();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove stamp');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render file status badge
  const renderStatusBadge = (file) => {
    // The viewer denies inspection for legacy-retired and retrieval-failed
    // references even when a historical "verified" stamp remains on record.
    // Use that SAME access assessment for the badge; a verified history
    // must never be mistaken for currently inspectable certificate evidence.
    if (isFileUnavailableForInspection(file)) {
      const replacementRequired = file.retention_assessment?.replacement_state === 'replacement_required';
      return (
        <Badge className="bg-red-50 text-red-700 border border-red-200 text-xs">
          {replacementRequired
            ? 'Evidence unavailable - re-upload and verification required'
            : 'Evidence unavailable — recovery review needed'}
        </Badge>
      );
    }
    // Show verified badge prominently if stamped
    // IMPORTANT: Exclude "not_verified" which is a placeholder value, not an actual stamp
    const hasValidStamp = file.stamped_file_url || 
      (file.verification_stamp && 
       file.verification_stamp !== 'not_verified' && 
       file.verification_stamp !== '');
    
    if (hasValidStamp) {
      const stampType = typeof file.verification_stamp === 'object' 
        ? file.verification_stamp?.stamp_type 
        : file.verification_stamp;
      
      // Badge colors based on stamp type
      const badgeStyles = {
        document_verified: 'bg-sky-50 text-sky-700 border-sky-200',
        original_seen: 'bg-purple-50 text-purple-700 border-purple-200',
        copy_verified: 'bg-amber-50 text-amber-700 border-amber-200',
        online_check: 'bg-sky-50 text-sky-700 border-sky-200', // Legacy
      };
      
      const badgeLabels = {
        document_verified: 'Verified',
        original_seen: 'Original Seen',
        copy_verified: 'Copy Verified',
        online_check: 'Verified',
      };
      
      const badgeClass = badgeStyles[stampType] || badgeStyles.document_verified;
      const badgeLabel = badgeLabels[stampType] || 'Verified';
      
      return (
        <div className="flex items-center gap-1.5">
          <Badge className={`text-xs border flex items-center gap-1 ${badgeClass}`}>
            <Stamp className="h-3 w-3" />
            <span>{badgeLabel}</span>
          </Badge>
        </div>
      );
    }
    if (file.currently_verified || file.verified) {
      return <Badge className="bg-green-100 text-green-700 text-xs">Verified</Badge>;
    }
    if (file.rejected) {
      return <Badge className="bg-red-100 text-red-700 text-xs">Rejected</Badge>;
    }
    if (file.extraction_status?.status === 'awaiting_review') {
      return <Badge className="bg-purple-100 text-purple-700 text-xs">Extraction Review</Badge>;
    }
    if (file.status === 'uploaded') {
      return <Badge className="bg-blue-100 text-blue-700 text-xs">Awaiting Review</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-600 text-xs">{file.status}</Badge>;
  };

  // Render historical file status badge
  const renderHistoricalBadge = (file) => {
    const status = file.status;
    if (status === 'superseded') {
      return <Badge className="bg-amber-100 text-amber-700 text-xs">Superseded</Badge>;
    }
    if (status === 'uploaded_in_error') {
      return <Badge className="bg-red-100 text-red-700 text-xs">Uploaded in Error</Badge>;
    }
    if (status === 'rejected') {
      return <Badge className="bg-red-100 text-red-700 text-xs">Rejected</Badge>;
    }
    if (status === 'moved') {
      return <Badge className="bg-blue-100 text-blue-700 text-xs">Moved</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-600 text-xs">{status}</Badge>;
  };

  const stateBadgeStyles = {
    available: 'bg-green-50 text-green-700 border-green-200',
    retired: 'bg-amber-50 text-amber-700 border-amber-200',
    unavailable: 'bg-red-50 text-red-700 border-red-200',
    retrieval_failed: 'bg-red-50 text-red-700 border-red-200',
    legacy_storage_retired: 'bg-amber-50 text-amber-700 border-amber-200',
    unknown: 'bg-gray-50 text-gray-600 border-gray-200',
    verified: 'bg-green-50 text-green-700 border-green-200',
    historical_verified_bytes_unavailable: 'bg-amber-50 text-amber-700 border-amber-200',
    unverified: 'bg-gray-50 text-gray-600 border-gray-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    active_retention: 'bg-sky-50 text-sky-700 border-sky-200',
    review_due: 'bg-amber-50 text-amber-700 border-amber-200',
    destruction_due: 'bg-red-50 text-red-700 border-red-200',
    legal_hold_active: 'bg-purple-50 text-purple-700 border-purple-200',
    retention_exception_active: 'bg-violet-50 text-violet-700 border-violet-200',
    binary_destroyed_metadata_retained: 'bg-gray-50 text-gray-600 border-gray-200',
    fully_destroyed_or_anonymised: 'bg-gray-50 text-gray-600 border-gray-200',
    not_required: 'bg-green-50 text-green-700 border-green-200',
    not_assessed: 'bg-gray-50 text-gray-600 border-gray-200',
    replacement_required: 'bg-red-50 text-red-700 border-red-200',
    replacement_requested: 'bg-amber-50 text-amber-700 border-amber-200',
    replacement_completed: 'bg-green-50 text-green-700 border-green-200',
    superseded: 'bg-gray-50 text-gray-600 border-gray-200',
  };

  const formatStateLabel = (value) => String(value || 'unknown').replace(/_/g, ' ');

  const renderAssessmentBadges = (file) => {
    const assessment = file.retention_assessment;
    if (!assessment) return null;
    const badges = [
      ['Availability', assessment.storage_availability],
      ['Verification', assessment.verification_state],
      ['Retention', assessment.retention_status],
      ['Replacement', assessment.replacement_state],
    ];
    return (
      <div className="mt-3 space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {badges.map(([label, value]) => (
            <Badge key={label} variant="outline" className={`text-[10px] capitalize ${stateBadgeStyles[value] || stateBadgeStyles.unknown}`}>
              {label}: {formatStateLabel(value)}
            </Badge>
          ))}
        </div>
        {assessment.policy_notice && (
          <p className="text-xs text-amber-700">
            {assessment.policy_notice}
          </p>
        )}
        {(assessment.storage_availability === 'retired' || assessment.storage_availability === 'legacy_storage_retired') && (
          <p className="text-xs text-amber-700">
            Historical storage location is retired. Whether a separate stored copy survives still needs verification.
          </p>
        )}
        {(assessment.retention_rule_code || assessment.rule_category) && (
          <p className="text-xs text-text-muted">
            Retention: {formatStateLabel(assessment.rule_category)} {assessment.retention_rule_code || ''} {assessment.retention_rule_version ? `v${assessment.retention_rule_version}` : ''}
            {assessment.trigger_date ? ` | Trigger ${formatBackendDate(assessment.trigger_date, { format: 'short' })}` : ''}
            {assessment.review_due_at ? ` | Review ${formatBackendDate(assessment.review_due_at, { format: 'short' })}` : ''}
            {assessment.destruction_due_at ? ` | Destroy ${formatBackendDate(assessment.destruction_due_at, { format: 'short' })}` : ''}
          </p>
        )}
        {assessment.replacement_recommendation?.explanation && (
          <p className="text-xs text-text-muted">
            {assessment.replacement_recommendation.explanation}
          </p>
        )}
      </div>
    );
  };

  // Requirement categories for move action
  const requirementCategories = [
    { id: 'right_to_work_documents', label: 'Right to Work Evidence' },
    { id: 'dbs_certificate', label: 'DBS Certificate' },
    { id: 'identity_documents', label: 'Identity Documents' },
    { id: 'proof_of_address', label: 'Proof of Address' },
    { id: 'professional_registration', label: 'Professional Registration' },
    { id: 'training_certificates', label: 'Training Certificates' },
    { id: 'references', label: 'References' },
    { id: 'other', label: 'Other Documents' }
  ].filter(cat => cat.id !== requirementKey);

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-600" />
              {requirementTitle || 'Requirement Files'}
            </SheetTitle>
            <SheetDescription>
              View and manage all files for this requirement
            </SheetDescription>
          </SheetHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filesData ? (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-text-primary">{filesData.active_file_count}</p>
                  <p className="text-xs text-text-muted">Active</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{filesData.verified_count}</p>
                  <p className="text-xs text-text-muted">Verified</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">{filesData.pending_review_count}</p>
                  <p className="text-xs text-text-muted">Pending</p>
                </div>
              </div>

              {/* Multi-file info */}
              {filesData.multi_file_config?.required_count && (
                <div className={`p-3 rounded-lg border ${
                  filesData.verified_count >= filesData.multi_file_config.required_count
                    ? 'bg-green-50 border-green-200'
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  <p className="text-sm font-medium">
                    {filesData.verified_count >= filesData.multi_file_config.required_count ? (
                      <span className="text-green-700 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        {filesData.verified_count}/{filesData.multi_file_config.required_count} required documents verified
                      </span>
                    ) : (
                      <span className="text-amber-700 flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        {filesData.verified_count}/{filesData.multi_file_config.required_count} required documents verified
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Actions Bar */}
              {!isAuditor && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => onUpload && onUpload(requirementKey, requirementTitle)}
                    className="flex-1 bg-primary hover:bg-primary-hover text-white rounded-lg"
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Upload
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onRequest && onRequest(requirementKey, requirementTitle)}
                    className="flex-1 rounded-lg"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Request
                  </Button>
                </div>
              )}

              {/* Active Files */}
              <div>
                <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-green-600" />
                  Active Files ({filesData.active_file_count})
                </h3>
                
                {filesData.active_files.length === 0 ? (
                  <div className="p-4 bg-gray-50 rounded-lg text-center text-sm text-text-muted">
                    No active files uploaded
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filesData.active_files.map((file) => (
                      <div 
                        key={file.file_id}
                        className="p-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors"
                        data-testid={`active-file-${file.file_id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            {/* File Name & Status */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-text-primary truncate">
                                {file.file_name}
                              </p>
                              {renderStatusBadge(file)}
                            </div>
                            
                            {/* Metadata */}
                            <div className="mt-2 space-y-1 text-xs text-text-muted">
                              <p>
                                <span className="font-medium">Uploaded:</span>{' '}
                                {formatBackendDate(file.uploaded_at, { format: 'medium' })}
                                {file.uploaded_by && ` by ${file.uploaded_by}`}
                              </p>
                              {file.source_type && (
                                <p>
                                  <span className="font-medium">Source:</span>{' '}
                                  {file.source_type === 'manual_upload' ? 'Manual Upload' :
                                   file.source_type === 'form_submission' ? 'Form Submission' :
                                   file.source_type === 'request_response' ? 'Request Response' :
                                   file.source_type}
                                </p>
                              )}
                              {file.expiry_date && (
                                <p>
                                  <span className="font-medium">Expires:</span>{' '}
                                  {formatBackendDate(file.expiry_date, { format: 'medium' })}
                                </p>
                              )}
                              {file.document_date && (
                                <p>
                                  <span className="font-medium">Document Date:</span>{' '}
                                  {formatBackendDate(file.document_date, { format: 'medium' })}
                                </p>
                              )}
                              {/* Show verification details only if NOT already shown via stamp badge */}
                              {file.currently_verified && file.verified_by && !file.stamped_file_url && !file.verification_stamp && (
                                <p className="text-green-600">
                                  <span className="font-medium">Verified:</span>{' '}
                                  {formatBackendDate(file.verified_at, { format: 'medium' })} by {file.verified_by}
                                </p>
                              )}
                            </div>

                            {/* Extraction Status */}
                            {file.extraction_status && (
                              <div className="mt-2 p-2 bg-purple-50 rounded-lg text-xs">
                                <p className="font-medium text-purple-700">
                                  Extraction: {file.extraction_status.status}
                                </p>
                                {file.extraction_status.reviewed_at && (
                                  <p className="text-purple-600">
                                    Reviewed {formatBackendDate(file.extraction_status.reviewed_at, { format: 'short' })}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Request Linkage */}
                            {file.request_linkage && (
                              <div className="mt-2 p-2 bg-blue-50 rounded-lg text-xs">
                                <p className="text-blue-700">
                                  <span className="font-medium">From Request:</span>{' '}
                                  {file.request_linkage.request_source === 'scheduled' ? 'Scheduled Request' : 'Manual Request'}
                                </p>
                                {file.request_linkage.submitted_at && (
                                  <p className="text-blue-600">
                                    Submitted {formatBackendDate(file.request_linkage.submitted_at, { format: 'short' })}
                                  </p>
                                )}
                              </div>
                            )}
                            {renderAssessmentBadges(file)}
                          </div>

                          {/* Actions Menu */}
                          <DocumentActionMenu
                            file={file}
                            onView={isFileUnavailableForInspection(file) ? null : () => handleViewFile(file)}
                            onDownload={isFileUnavailableForInspection(file) ? null : () => handleDownloadFile(file)}
                            onVerify={() => handleVerify(file.file_id)}
                            onRequestReplacement={() => setActionDialog({ open: true, type: 'reject', file })}
                            onExtractReview={isFileUnavailableForInspection(file) ? null : () => onExtractReview && onExtractReview(file.file_id)}
                            onRemoveStamp={isFileUnavailableForInspection(file) ? null : () => setActionDialog({ open: true, type: 'remove_stamp', file })}
                            onMarkUploadedInError={() => setActionDialog({ open: true, type: 'uploaded_in_error', file })}
                            onSupersede={() => setActionDialog({ open: true, type: 'supersede', file })}
                            onMoveCategory={() => setActionDialog({ open: true, type: 'move_category', file })}
                            isAuditor={isAuditor}
                            isProcessing={isSubmitting}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Historical Files - Collapsed */}
              {filesData.historical_files.length > 0 && (
                <div>
                  <button
                    onClick={() => setHistoricalExpanded(!historicalExpanded)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                    data-testid="toggle-historical-files"
                  >
                    <span className="font-medium text-text-muted flex items-center gap-2">
                      <Archive className="h-4 w-4" />
                      Historical Files ({filesData.historical_file_count})
                    </span>
                    {historicalExpanded ? (
                      <ChevronDown className="h-4 w-4 text-text-muted" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-text-muted" />
                    )}
                  </button>

                  {historicalExpanded && (
                    <div className="mt-3 space-y-2">
                      {filesData.historical_files.map((file) => (
                        <div 
                          key={file.file_id}
                          className="p-3 bg-gray-50 border border-gray-100 rounded-lg opacity-75"
                          data-testid={`historical-file-${file.file_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-text-muted truncate">
                                  {file.file_name}
                                </p>
                                {renderHistoricalBadge(file)}
                              </div>
                              <p className="text-xs text-text-muted mt-1">
                                {formatBackendDate(file.uploaded_at, { format: 'medium' })}
                              </p>
                              {file.supersede_reason && (
                                <p className="text-xs text-amber-600 mt-1">
                                  Reason: {file.supersede_reason}
                                </p>
                              )}
                              {file.uploaded_in_error_reason && (
                                <p className="text-xs text-red-600 mt-1">
                                  Reason: {file.uploaded_in_error_reason}
                                </p>
                              )}
                              {file.rejection_reason && (
                                <p className="text-xs text-red-600 mt-1">
                                  Reason: {file.rejection_reason}
                                </p>
                              )}
                              {file.move_reason && (
                                <p className="text-xs text-blue-600 mt-1">
                                  Moved to: {file.moved_to} - {file.move_reason}
                                </p>
                              )}
                              {renderAssessmentBadges(file)}
                            </div>
                            {!isFileUnavailableForInspection(file) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewFile(file)}
                                className="h-8 w-8 p-0"
                                data-testid={`view-historical-file-${file.file_id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Request History - Collapsed */}
              {filesData.requests && filesData.requests.length > 0 && (
                <div>
                  <button
                    onClick={() => setRequestHistoryExpanded(!requestHistoryExpanded)}
                    className="w-full flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                    data-testid="toggle-request-history"
                  >
                    <span className="font-medium text-blue-700 flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      Request History ({filesData.request_count})
                    </span>
                    {requestHistoryExpanded ? (
                      <ChevronDown className="h-4 w-4 text-blue-600" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-blue-600" />
                    )}
                  </button>

                  {requestHistoryExpanded && (
                    <div className="mt-3 space-y-2">
                      {filesData.requests.map((req, idx) => (
                        <div 
                          key={req.request_id || idx}
                          className={`p-3 border rounded-lg ${
                            req.status === 'completed' ? 'bg-green-50 border-green-200' :
                            req.status === 'expired' ? 'bg-gray-50 border-gray-200' :
                            'bg-blue-50 border-blue-200'
                          }`}
                          data-testid={`request-${req.request_id || idx}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Badge className={`text-[10px] px-1.5 py-0 ${
                                  req.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  req.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                  req.status === 'clicked' ? 'bg-purple-100 text-purple-700' :
                                  req.status === 'expired' ? 'bg-gray-100 text-gray-500' :
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {req.status === 'clicked' ? 'Viewed' : req.status}
                                </Badge>
                                {req.source === 'scheduled' && (
                                  <Badge className="text-[10px] px-1 py-0 bg-gray-100 text-gray-500">
                                    Auto
                                  </Badge>
                                )}
                                {req.is_replacement && (
                                  <Badge className="text-[10px] px-1 py-0 bg-amber-100 text-amber-700">
                                    Replacement
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-text-muted mt-1 space-y-0.5">
                                {req.sent_at && (
                                  <p>Sent: {formatBackendDate(req.sent_at, { format: 'medium' })}</p>
                                )}
                                {req.viewed_at && (
                                  <p>Viewed: {formatBackendDate(req.viewed_at, { format: 'medium' })}</p>
                                )}
                                {req.submitted_at && (
                                  <p>Submitted: {formatBackendDate(req.submitted_at, { format: 'medium' })}</p>
                                )}
                                {req.reminder_count > 0 && (
                                  <p className="text-amber-600">{req.reminder_count} reminder(s) sent</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="py-12 text-center text-text-muted">
              Failed to load files
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Action Dialogs */}
      <Dialog 
        open={actionDialog.open} 
        onOpenChange={(open) => !open && setActionDialog({ open: false, type: null, file: null })}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === 'uploaded_in_error' && 'Mark as Uploaded in Error'}
              {actionDialog.type === 'supersede' && 'Supersede File'}
              {actionDialog.type === 'move_category' && 'Move to Different Category'}
              {actionDialog.type === 'reject' && 'Request Replacement'}
              {actionDialog.type === 'remove_stamp' && 'Remove Verification Stamp'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.file?.file_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {actionDialog.type === 'move_category' && (
              <div className="space-y-2">
                <Label>New Category *</Label>
                <Select value={newRequirementId} onValueChange={setNewRequirementId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {requirementCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {actionDialog.type !== 'remove_stamp' && (
              <div className="space-y-2">
                <Label>Reason *</Label>
                <Textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={
                    actionDialog.type === 'uploaded_in_error' ? 'Why was this file uploaded in error?' :
                    actionDialog.type === 'supersede' ? 'Why is this file being superseded?' :
                    actionDialog.type === 'move_category' ? 'Why is this file being moved?' :
                    actionDialog.type === 'reject' ? 'Why does this file need to be replaced? (Employee will be notified)' :
                    'Enter reason...'
                  }
                  className="min-h-[80px]"
                />
              </div>
            )}

            {actionDialog.type === 'uploaded_in_error' && (
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-xs text-amber-700">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  This will remove the file from the active set but preserve it in history for audit purposes.
                </p>
              </div>
            )}

            {actionDialog.type === 'supersede' && (
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-xs text-amber-700">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  This file will be marked as superseded. You can then upload a replacement.
                </p>
              </div>
            )}
            
            {actionDialog.type === 'reject' && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  The employee will be notified via email and the upload slot will become available immediately.
                </p>
              </div>
            )}

            {actionDialog.type === 'remove_stamp' && (
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-xs text-amber-700">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  This will remove the CQC stamp and revert to the original unstamped version.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setActionDialog({ open: false, type: null, file: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (actionDialog.type === 'uploaded_in_error') {
                  handleMarkUploadedInError(actionDialog.file?.file_id);
                } else if (actionDialog.type === 'supersede') {
                  handleSupersede(actionDialog.file?.file_id);
                } else if (actionDialog.type === 'move_category') {
                  handleMoveCategory(actionDialog.file?.file_id);
                } else if (actionDialog.type === 'reject') {
                  handleRequestReplacement(actionDialog.file?.file_id);
                } else if (actionDialog.type === 'remove_stamp') {
                  handleRemoveStamp(actionDialog.file?.file_id);
                }
              }}
              disabled={
                isSubmitting ||
                ((actionDialog.type !== 'remove_stamp') && !actionReason.trim()) ||
                (actionDialog.type === 'move_category' && !newRequirementId)
              }
              className={
                actionDialog.type === 'reject' || actionDialog.type === 'uploaded_in_error'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-primary hover:bg-primary-hover text-white'
              }
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {actionDialog.type === 'uploaded_in_error' && 'Mark as Error'}
              {actionDialog.type === 'supersede' && 'Supersede'}
              {actionDialog.type === 'move_category' && 'Move File'}
              {actionDialog.type === 'reject' && 'Request Replacement'}
              {actionDialog.type === 'remove_stamp' && 'Remove Stamp'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

